import { fetchFeedbacks, updateVisibilityStatus } from '@/store/slices/feedbackSlice';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { useTranslation } from 'react-i18next';
import Toaster from '@/components/Toaster';

const AdminFeedbackList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("feedback");
  
  const { feedbacks, pagination, loading, error } = useSelector((state) => state.feedback);
  const [currentPage, setCurrentPage] = useState(1);
  const [previewImage, setPreviewImage] = useState(null);

  // Reference for the intersection observer
  const observer = useRef();

  // Fetch data whenever currentPage changes
  useEffect(() => {
    dispatch(fetchFeedbacks(currentPage));
  }, [dispatch, currentPage]);

  const handleToggle = (id, isCurrentlyFeatured) => {
    dispatch(updateVisibilityStatus({ id, newStatus: !isCurrentlyFeatured }));
  };

  // This callback function attaches the observer to the final element in the list
  const lastFeedbackElementRef = useCallback((node) => {
    if (loading) return; // Do not trigger fetch while already loading
    if (observer.current) observer.current.disconnect(); 

    observer.current = new IntersectionObserver((entries) => {
      // If the marker is intersecting the viewport and we have more pages to load
      if (entries[0].isIntersecting && pagination && currentPage < pagination.last_page) {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, pagination, currentPage]);

  const handleDownloadImage = async (imageUrl) => {
    try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;

        const fileName =
            imageUrl.split("/").pop()?.split("?")[0] || "attachment";

        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        Toaster.error(t("failed_to_download_image"));
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center mt-1 mb-2">
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`w-5 h-5 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">{t("admin_feedback_management")}</h1>
        <p className="text-gray-600 mt-2">
          {t("select_featured_feedback")}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-md">
          {t("error_prefix")} {typeof error === 'string' ? error : t("error_fetching_data")}
        </div>
      )}

      {/* Initial load state (No items yet, but loading) */}
      {loading && feedbacks.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-3 text-gray-600 font-medium">{t("loading_feedbacks")}</span>
        </div>
      ) : (
        <>
          {(!feedbacks || feedbacks.length === 0) && !error ? (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900">{t("no_feedbacks_found_title")}</h3>
              <p className="text-gray-500 mt-1">{t("no_feedbacks_available")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedbacks?.map((feedback, index) => {
                const isFeatured = feedback.is_featured === 'yes';
                
                // Determine if this is the very last element in the array
                const isLastElement = feedbacks.length === index + 1;

                return (
                  <div 
                    key={feedback.id} 
                    // Attach the ref ONLY to the last item in the list
                    ref={isLastElement ? lastFeedbackElementRef : null}
                    className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-all"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{feedback.registered_company_name || feedback.first_name+" "+feedback.last_name}</h3>
                      {renderStars(feedback.star_rating)}
                      <p className="text-gray-700 italic">{feedback.description}</p>
                    </div>
                    {/* 3. Uploaded Image Attachments Section */}
                    {feedback.images && feedback.images.length > 0 && (
                      <div>
                        <div className="flex flex-wrap gap-4">
                          {feedback.images.map((imgSrc, imgIndex) => (
                            <img 
                              key={imgIndex} 
                              src={imgSrc?.image_url} 
                              alt={t("attachment_alt", { index: imgIndex + 1 })} 
                              onClick={() => setPreviewImage(imgSrc?.image_url)}
                              className="w-24 h-24 object-cover rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4">
                      <span 
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          isFeatured
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {isFeatured ? t("published") : t("hidden")}
                      </span>

                      <button
                        onClick={() => handleToggle(feedback.id, isFeatured)}
                        disabled={!isFeatured && feedback.star_rating < 3}
                        className={`px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors w-40 ${
                          isFeatured
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-green-800 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed'
                        }`}
                      >
                        {isFeatured ? t("hide") : t("publish")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Spinner at the bottom when loading subsequent pages */}
          {loading && feedbacks.length > 0 && (
            <div className="flex justify-center items-center py-6 mt-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
              <span className="ml-3 text-gray-500 text-sm font-medium">{t("loading_more")}</span>
            </div>
          )}
        </>
      )}

      {previewImage && (
          <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
              onClick={() => setPreviewImage(null)}
          >
              <div 
                  className="relative max-w-3xl max-h-[85vh] p-2 bg-white rounded-xl shadow-2xl overflow-hidden mx-4 flex flex-col items-center"
                  onClick={(e) => e.stopPropagation()} 
              >
                  <div className="absolute top-3 right-3 flex gap-2 z-50">
                      <button
                          type="button"
                          onClick={() => handleDownloadImage(previewImage)}
                          className="p-2 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors"
                          title={t("download_image")}
                      >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>

                      <button
                          type="button"
                          onClick={() => setPreviewImage(null)}
                          className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                          title={t("close")}
                      >
                          <XMarkIcon className="h-5 w-5" />
                      </button>
                  </div>
                  <img src={previewImage} alt={t("attachment_full_preview")} className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
              </div>
          </div>
      )}
    </div>
  );
};

export default AdminFeedbackList;